import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  ViewChild
} from '@angular/core';
import { exportToExcel } from '@app/shared/utilities/excel-export';
import { VolumeTableColumns } from '@app/shared/constants';
import { VolumeLocation } from '@app/shared/models/volume-dashboard.model';
import { Filter, SortState } from '@app/shared/models/common.model';
import { FilterType, LocationStatusEnum } from '@app/shared/enums/common.enums';
import { DatetimeService } from '@app/shared/services/datetime.service';
import { FilterDrawerComponent } from '@app/pages/authenticated/components/filter-drawer/filter-drawer.component';
import { LOCATION_STATUS_META, statusOptions } from '@app/shared/utilities/status.meta';
import { TranslateService } from '@ngx-translate/core';

// Owns location-specific filters, totals, and export around the shared table.
@Component({
  selector: 'app-volume-table',
  templateUrl: './volume-table.component.html',
  styleUrl: './volume-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class VolumeTableComponent implements OnChanges {
  @Input() locations: VolumeLocation[] = [];
  @ViewChild(FilterDrawerComponent) private drawer?: FilterDrawerComponent;

  readonly columns = VolumeTableColumns;
  readonly initialSort: SortState = { key: 'total', dir: 'desc' };

  filters: Map<string, Filter> = new Map([
    ['provinceId', { type: FilterType.MultiSelect, options: [] }],
    ['statusId', { type: FilterType.MultiSelect, options: [] }],
  ]);
  private selectedProvinces: string[] = [];
  private selectedStatuses: number[] = [];

  searchText = '';
  rows: VolumeLocation[] = [];
  footerRow: Record<string, any> | null = null;

  constructor(
    private datetime: DatetimeService,
    private translate: TranslateService
  ) {}

  ngOnChanges(): void {
    this.updateFilterOptions();
    this.recompute();
  }

  clearSearch(): void {
    this.searchText = '';
    this.recompute();
  }

  onSearch(value: string): void {
    this.searchText = value ?? '';
    this.recompute();
  }

  get activeFilterCount(): number {
    return this.selectedProvinces.length + this.selectedStatuses.length;
  }

  get hasActiveFilters(): boolean {
    return this.activeFilterCount > 0;
  }

  toggleFilters(): void {
    this.drawer?.toggleFilters();
  }

  applyFilters(value: any): void {
    this.selectedProvinces = (value?.provinceId ?? []).map((o: any) => o.id);
    this.selectedStatuses = (value?.statusId ?? []).map((o: any) => o.id);
    this.drawer?.toggleFilters();
    this.recompute();
  }

  clearFilters(isOpen: boolean = true): void {
    this.selectedProvinces = [];
    this.selectedStatuses = [];
    if (!isOpen) this.drawer?.filtersForm?.reset();
    this.recompute();
  }

  abs(value: number): number {
    return Math.abs(value);
  }

  statusClass(statusId: number): string {
    return LOCATION_STATUS_META[statusId].css;
  }

  statusLabel(statusId: number): string {
    return LOCATION_STATUS_META[statusId].key;
  }

  isActive(statusId: number): boolean {
    return statusId === LocationStatusEnum.Active;
  }

  private updateFilterOptions(): void {
    const provinces = Array.from(
      new Set((this.locations ?? []).map((l) => l.provinceCode).filter(Boolean))
    ).sort();
    const statusIds = Array.from(
      new Set((this.locations ?? []).map((location) => location.statusId))
    ).sort((left, right) => left - right);

    this.filters.get('provinceId')!.options = provinces.map((province) => ({
      id: province,
      value: province,
    }));
    this.filters.get('statusId')!.options = statusOptions(LOCATION_STATUS_META)
      .filter((option) => statusIds.includes(option.id));
  }

  private columnTotal(key: keyof VolumeLocation): number {
    return this.rows.reduce((sum, r) => sum + (Number(r[key]) || 0), 0);
  }

  export(): void {
    const data = this.rows.map((r) => ({
      'Partner Shop': r.name,
      City: r.city,
      Province: r.provinceCode,
      'Start Date': r.startDate ? this.datetime.formatDateInUserTimeZone(r.startDate, 'yyyy-MM-dd') : '',
      Status: this.translate.instant(this.statusLabel(r.statusId)),
      HAL: Math.round(r.hal),
      Subscription: Math.round(r.subscription),
      'Drop-off': Math.round(r.dropOff),
      Outbound: Math.round(r.outbound),
      Total: Math.round(r.total),
      'Prior total': Math.round(r.priorTotal),
      Diff: Math.round(r.diffAbsolute),
    }));
    if (this.rows.length) {
      data.push({
        'Partner Shop': `All (${this.rows.length})`,
        City: '',
        Province: '',
        'Start Date': '',
        Status: '',
        HAL: Math.round(this.columnTotal('hal')),
        Subscription: Math.round(this.columnTotal('subscription')),
        'Drop-off': Math.round(this.columnTotal('dropOff')),
        Outbound: Math.round(this.columnTotal('outbound')),
        Total: Math.round(this.columnTotal('total')),
        'Prior total': Math.round(this.columnTotal('priorTotal')),
        Diff: Math.round(this.columnTotal('diffAbsolute')),
      });
    }
    exportToExcel(data, 'volume-by-location.xlsx', 'Volume');
  }

  private recompute(): void {
    let rows = this.locations ?? [];

    if (this.selectedProvinces.length) {
      rows = rows.filter((r) => this.selectedProvinces.includes(r.provinceCode));
    }

    if (this.selectedStatuses.length) {
      rows = rows.filter((row) => this.selectedStatuses.includes(row.statusId));
    }

    const q = this.searchText.trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) => `${r.name} ${r.city} ${r.provinceCode}`.toLowerCase().includes(q));
    }

    this.rows = rows;
    this.footerRow = this.buildFooter();
  }

  private buildFooter(): Record<string, any> | null {
    if (!this.rows.length) return null;
    return {
      count: this.rows.length,
      hal: this.columnTotal('hal'),
      subscription: this.columnTotal('subscription'),
      dropOff: this.columnTotal('dropOff'),
      outbound: this.columnTotal('outbound'),
      total: this.columnTotal('total'),
      priorTotal: this.columnTotal('priorTotal'),
      diffAbsolute: this.columnTotal('diffAbsolute'),
    };
  }
}
